import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { useAuth } from '../AuthContext';
import {
  CmsProject,
  CmsTestimonial,
  CmsInfluencerVideo,
  CmsPastProject,
  CmsSiteSettings,
} from '../types/cms';
import { WebsiteCmsService } from '../services/websiteCmsService';
import { ProjectSelectorBar } from '../components/cms/ProjectSelectorBar';
import { NewProjectModal } from '../components/cms/NewProjectModal';
import { ProjectOverviewTab } from '../components/cms/ProjectOverviewTab';
import { ProjectGalleryTab } from '../components/cms/ProjectGalleryTab';
import { ProjectAmenitiesTab } from '../components/cms/ProjectAmenitiesTab';
import { ProjectSeoTab } from '../components/cms/ProjectSeoTab';
import { ProjectFaqTab } from '../components/cms/ProjectFaqTab';
import { ProjectReelsTab } from '../components/cms/ProjectReelsTab';
import TestimonialsManagerTab from '../components/cms/TestimonialsManagerTab';
import InfluencerVideosManagerTab from '../components/cms/InfluencerVideosManagerTab';
import PastProjectsManagerTab from '../components/cms/PastProjectsManagerTab';
import SiteSettingsManagerTab from '../components/cms/SiteSettingsManagerTab';

const GOLD = '#9d6f32';
const DARK = '#1a1a1a';
const BORDER = '#e8e8e8';

export default function WebsiteCmsPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [mainTab, setMainTab] = useState(0); // 0: Projects, 1: Testimonials, 2: Influencers, 3: Past Projects, 4: Site Settings
  
  // Datasets
  const [projects, setProjects] = useState<CmsProject[]>([]);
  const [projectDrafts, setProjectDrafts] = useState<Record<string, CmsProject>>({});
  const [activeProjectSlug, setActiveProjectSlug] = useState<string>('swarna-avsar');
  const [currentProject, setCurrentProject] = useState<CmsProject | null>(null);
  const [testimonials, setTestimonials] = useState<CmsTestimonial[]>([]);
  const [influencerVideos, setInfluencerVideos] = useState<CmsInfluencerVideo[]>([]);
  const [pastProjects, setPastProjects] = useState<CmsPastProject[]>([]);
  const [siteSettings, setSiteSettings] = useState<CmsSiteSettings | null>(null);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'info' | 'error' } | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const loadAllCmsData = async () => {
      setLoading(true);
      try {
      const [projList, drafts, testList, infList, pastList, settingsData] = await Promise.all([
        WebsiteCmsService.getAllProjects(),
        WebsiteCmsService.getProjectDrafts(),
        WebsiteCmsService.getAllTestimonials(),
        WebsiteCmsService.getAllInfluencerVideos(),
        WebsiteCmsService.getAllPastProjects(),
        WebsiteCmsService.getSiteSettings(),
      ]);

      const allProjects = [...projList];
      Object.values(drafts).forEach((draft) => {
        if (!allProjects.some((project) => project.slug === draft.slug)) allProjects.push(draft);
      });
      setProjects(allProjects);
      setProjectDrafts(drafts);
      setTestimonials(testList);
      setInfluencerVideos(infList);
      setPastProjects(pastList);
      setSiteSettings(settingsData);

      if (allProjects.length > 0) {
        const found = allProjects.find((p) => p.slug === activeProjectSlug) || allProjects[0];
        setActiveProjectSlug(found.slug);
        setCurrentProject(JSON.parse(JSON.stringify(drafts[found.slug] || found)));
      }
      } catch {
        setLoadError('Unable to load website projects. Check your connection and access, then reopen this page.');
      } finally {
        setLoading(false);
      }
    };
    loadAllCmsData();
  }, []);

  const handleSelectProject = (slug: string) => {
    if (hasUnsavedChanges && !window.confirm('Discard unsaved project changes?')) return;
    const found = projects.find((p) => p.slug === slug);
    if (found) {
      setActiveProjectSlug(slug);
      setCurrentProject(JSON.parse(JSON.stringify(projectDrafts[slug] || found)));
      setHasUnsavedChanges(false);
    }
  };

  const handleOpenProjectSeo = (slug: string) => {
    if (hasUnsavedChanges && !window.confirm('Discard unsaved project changes?')) return;
    const found = projects.find((project) => project.slug === slug);
    if (!found) return;
    setCurrentProject(JSON.parse(JSON.stringify(projectDrafts[slug] || found)));
    setActiveProjectSlug(slug);
    setHasUnsavedChanges(false);
    setMainTab(0);
    setActiveTab(4);
  };

  const handleProjectUpdate = (updated: CmsProject) => {
    setCurrentProject(updated);
    setHasUnsavedChanges(true);
  };

  const handleSaveDraft = async () => {
    if (!currentProject) return;
    setSaving(true);
    try {
      const saved = await WebsiteCmsService.saveProjectDraft(currentProject, currentUser?.email || 'Admin');
      setProjectDrafts((prev) => ({ ...prev, [saved.slug]: saved }));
      setHasUnsavedChanges(false);
      setNotification({ message: `${saved.name} draft saved. The live page has not changed.`, severity: 'success' });
    } catch {
      setNotification({ message: 'Draft could not be saved. Changes remain in this editor.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndPublish = async () => {
    if (!currentProject) return;
    const required = [
      !currentProject.name.trim() && 'project name',
      !currentProject.slug.trim() && 'URL slug',
      !currentProject.seo?.metaTitle?.trim() && 'page title',
      !currentProject.seo?.metaDescription?.trim() && 'page description',
      !currentProject.heroImage?.trim() && 'hero image',
    ].filter(Boolean);
    if (required.length) {
      setNotification({ message: `Complete these fields before publishing: ${required.join(', ')}.`, severity: 'error' });
      return;
    }
    const expectedCanonical = `https://www.swarnagroup.in/projects/${currentProject.slug}`;
    if (currentProject.seo.canonicalUrl && currentProject.seo.canonicalUrl !== expectedCanonical) {
      setNotification({ message: `Canonical URL must match this project page: ${expectedCanonical}`, severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const email = currentUser?.email || 'Admin';
      const saved = await WebsiteCmsService.saveProject({ ...currentProject, isPublished: true }, email);
      
      // Update projects list
      setProjects((prev) => {
        const idx = prev.findIndex((p) => p.slug === saved.slug);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = saved;
          return next;
        }
        return [...prev, saved];
      });

      setCurrentProject(saved);
      setProjectDrafts((prev) => {
        const next = { ...prev };
        delete next[saved.slug];
        return next;
      });
      setHasUnsavedChanges(false);
      setNotification({
        message: `${saved.name} saved to the website database. Open the live page to verify the result.`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Save failed', err);
      setNotification({
        message: 'Project was not saved. Your changes remain in this editor.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNewProject = async (newProj: CmsProject) => {
    if (projects.some((project) => project.slug === newProj.slug)) {
      setNotification({ message: 'A project with this URL slug already exists.', severity: 'error' });
      return;
    }
    try {
      const email = currentUser?.email || 'Admin';
      const saved = await WebsiteCmsService.saveProjectDraft({ ...newProj, isPublished: false }, email);
      setProjects((prev) => [...prev, saved]);
      setProjectDrafts((prev) => ({ ...prev, [saved.slug]: saved }));
      setActiveProjectSlug(saved.slug);
      setCurrentProject(saved);
      setHasUnsavedChanges(false);
      setNotification({ message: `New project "${saved.name}" saved as a draft.`, severity: 'info' });
    } catch {
      setNotification({ message: 'New project could not be saved.', severity: 'error' });
    }
  };

  const handleDeleteProject = async () => {
    if (!currentProject || projects.length <= 1) return;
    const confirmDelete = window.confirm(`Are you sure you want to delete "${currentProject.name}" from the website CMS?`);
    if (!confirmDelete) return;

    try {
      await WebsiteCmsService.deleteProject(currentProject.slug);
    } catch {
      setNotification({ message: 'Project could not be deleted.', severity: 'error' });
      return;
    }
    const remaining = projects.filter((p) => p.slug !== currentProject.slug);
    setProjects(remaining);
    setProjectDrafts((prev) => {
      const next = { ...prev };
      delete next[currentProject.slug];
      return next;
    });
    setActiveProjectSlug(remaining[0].slug);
    setCurrentProject(JSON.parse(JSON.stringify(remaining[0])));
    setHasUnsavedChanges(false);
    setNotification({
      message: `Project removed from CMS.`,
      severity: 'info',
    });
  };

  // Module handlers
  const handleSaveTestimonial = async (item: CmsTestimonial) => {
    try {
      const saved = await WebsiteCmsService.saveTestimonial(item);
      setTestimonials((prev) => [saved, ...prev.filter((t) => t.id !== saved.id)]);
      setNotification({ message: 'Customer testimonial saved to the website database.', severity: 'success' });
    } catch {
      setNotification({ message: 'Customer testimonial could not be saved.', severity: 'error' });
    }
  };

  const handleDeleteTestimonial = async (id: string) => {
    try {
      await WebsiteCmsService.deleteTestimonial(id);
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
      setNotification({ message: 'Testimonial removed.', severity: 'info' });
    } catch {
      setNotification({ message: 'Testimonial could not be removed.', severity: 'error' });
    }
  };

  const handleSaveInfluencerVideo = async (item: CmsInfluencerVideo) => {
    try {
      const saved = await WebsiteCmsService.saveInfluencerVideo(item);
      setInfluencerVideos((prev) => [saved, ...prev.filter((v) => v.id !== saved.id)]);
      setNotification({ message: 'Walkthrough video saved to the website database.', severity: 'success' });
    } catch {
      setNotification({ message: 'Walkthrough video could not be saved.', severity: 'error' });
    }
  };

  const handleDeleteInfluencerVideo = async (id: string) => {
    try {
      await WebsiteCmsService.deleteInfluencerVideo(id);
      setInfluencerVideos((prev) => prev.filter((v) => v.id !== id));
      setNotification({ message: 'Video removed.', severity: 'info' });
    } catch {
      setNotification({ message: 'Video could not be removed.', severity: 'error' });
    }
  };

  const handleSavePastProject = async (item: CmsPastProject) => {
    try {
      const saved = await WebsiteCmsService.savePastProject(item);
      setPastProjects((prev) => [saved, ...prev.filter((p) => p.id !== saved.id)]);
      setNotification({ message: 'Past project saved to the website database.', severity: 'success' });
    } catch {
      setNotification({ message: 'Past project could not be saved.', severity: 'error' });
    }
  };

  const handleDeletePastProject = async (id: string) => {
    try {
      await WebsiteCmsService.deletePastProject(id);
      setPastProjects((prev) => prev.filter((p) => p.id !== id));
      setNotification({ message: 'Past project removed.', severity: 'info' });
    } catch {
      setNotification({ message: 'Past project could not be removed.', severity: 'error' });
    }
  };

  const handleSaveSiteSettings = async (settings: CmsSiteSettings) => {
    try {
      const saved = await WebsiteCmsService.saveSiteSettings(settings);
      setSiteSettings(saved);
      setHasUnsavedChanges(false);
      setNotification({ message: 'Website settings saved to the database.', severity: 'success' });
    } catch {
      setNotification({ message: 'Website settings could not be saved.', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: DARK, mb: 0.5 }}>
          🌐 Website CMS & Content Engine
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
          Edit projects, videos, past work, and site settings for <code>swarnagroup.in</code>. Save drafts separately, then check the public page after publishing.
        </Typography>
      </Box>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {!loadError && projects.length === 0 && <Alert severity="info" sx={{ mb: 2 }} action={<Button onClick={() => setNewModalOpen(true)}>Create project</Button>}>No projects are stored in the website database yet.</Alert>}

      <Paper sx={{ mb: 3, p: 2, border: `1px solid ${BORDER}`, borderRadius: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Website page overview</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These checks use CRM data. Open each public page after publishing to verify what visitors see; indexing and Search Console results are not checked here.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Page</TableCell><TableCell>CMS status</TableCell><TableCell>Content checks</TableCell><TableCell>Last saved</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => {
                const edit = projectDrafts[project.slug] || project;
                const missing = [!edit.seo?.metaTitle && 'title', !edit.seo?.metaDescription && 'description', !edit.reraNumber && 'RERA', !edit.heroImage && 'hero image'].filter(Boolean);
                return <TableRow key={project.slug}>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{edit.name}</Typography><Typography variant="caption" color="text.secondary">/projects/{project.slug}</Typography></TableCell>
                  <TableCell><Chip size="small" label={projectDrafts[project.slug] ? (project.isPublished !== false ? 'Published + draft' : 'Draft saved') : project.isPublished !== false ? 'Published in CMS' : 'Draft only'} color={projectDrafts[project.slug] ? 'info' : project.isPublished !== false ? 'success' : 'default'} /></TableCell>
                  <TableCell>{missing.length ? `Missing: ${missing.join(', ')}` : 'Core fields present'}</TableCell>
                  <TableCell>{edit.updatedAt ? new Date(edit.updatedAt).toLocaleDateString('en-IN') : '—'}</TableCell>
                  <TableCell align="right"><Button size="small" onClick={() => handleOpenProjectSeo(project.slug)}>Edit page</Button><Button size="small" href={`https://www.swarnagroup.in/projects/${project.slug}`} target="_blank" rel="noopener noreferrer" disabled={project.isPublished === false}>Open public page</Button></TableCell>
                </TableRow>;
              })}
              <TableRow>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>Homepage</Typography><Typography variant="caption" color="text.secondary">/</Typography></TableCell>
                <TableCell><Chip size="small" label="Site settings" /></TableCell>
                <TableCell>{siteSettings?.seoTitle && siteSettings?.seoDescription ? 'Title and description present' : 'Title or description missing'}</TableCell>
                <TableCell>{siteSettings?.updatedAt ? new Date(siteSettings.updatedAt).toLocaleDateString('en-IN') : '—'}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => {
                  if (hasUnsavedChanges && !window.confirm('Discard unsaved changes?')) return;
                  setHasUnsavedChanges(false);
                  setMainTab(4);
                }}>Edit settings</Button><Button size="small" href="https://www.swarnagroup.in/" target="_blank" rel="noopener noreferrer">Open public page</Button></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Top Level Module Switcher */}
      <Paper sx={{ mb: 3, borderRadius: 2.5, border: `1px solid ${BORDER}`, bgcolor: '#ffffff', p: 0.5 }}>
        <Tabs
          value={mainTab}
          onChange={(_, val) => {
            if (hasUnsavedChanges && !window.confirm('Discard unsaved changes?')) return;
            if (hasUnsavedChanges && currentProject) {
              const saved = projectDrafts[currentProject.slug] || projects.find((project) => project.slug === currentProject.slug);
              if (saved) setCurrentProject(JSON.parse(JSON.stringify(saved)));
            }
            setHasUnsavedChanges(false);
            setMainTab(val);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 14,
              minHeight: 48,
              borderRadius: 2,
              mx: 0.5,
              color: '#555',
              '&.Mui-selected': { color: '#ffffff', bgcolor: GOLD },
            },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          <Tab icon={<ApartmentIcon sx={{ fontSize: 19 }} />} iconPosition="start" label="Active Projects" />
          <Tab icon={<VideoLibraryIcon sx={{ fontSize: 19 }} />} iconPosition="start" label="Video Testimonials" />
          <Tab icon={<PlayCircleFilledWhiteIcon sx={{ fontSize: 19 }} />} iconPosition="start" label="Creator Reels" />
          <Tab icon={<HistoryEduIcon sx={{ fontSize: 19 }} />} iconPosition="start" label="Heritage & Track Record" />
          <Tab icon={<SettingsSuggestIcon sx={{ fontSize: 19 }} />} iconPosition="start" label="Site Settings & Stats" />
        </Tabs>
      </Paper>

      {/* MAIN TAB 0: ACTIVE PROJECTS */}
      {mainTab === 0 && (
        <>
          {/* Project Selector Bar */}
          <ProjectSelectorBar
            projects={projects}
            currentProject={currentProject}
            onSelectProject={handleSelectProject}
            onOpenNewModal={() => setNewModalOpen(true)}
            onSaveAndPublish={handleSaveAndPublish}
            onSaveDraft={handleSaveDraft}
            onPreviewDraft={() => setPreviewOpen(true)}
            hasSavedDraft={Boolean(currentProject && projectDrafts[currentProject.slug])}
            onDeleteCurrentProject={handleDeleteProject}
            saving={saving}
            hasUnsavedChanges={hasUnsavedChanges}
          />

          {/* Main CMS Sub-Tabs */}
          {currentProject && (
            <Paper sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: `1px solid ${BORDER}`, bgcolor: '#fafafa', px: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, val) => setActiveTab(val)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 13.5,
                      minHeight: 52,
                      color: '#6b6b6b',
                      '&.Mui-selected': { color: GOLD },
                    },
                    '& .MuiTabs-indicator': { bgcolor: GOLD, height: 3 },
                  }}
                >
                  <Tab label="Overview & Pricing" />
                  <Tab label="Gallery & Plans" />
                  <Tab label="Highlights & Amenities" />
                  <Tab
                    label={`Creator Reels (${influencerVideos.filter((v) => v.projectSlug === currentProject.slug).length})`}
                  />
                  <Tab label="SEO & Schema Engine" />
                  <Tab label="Buyer FAQs" />
                </Tabs>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {activeTab === 0 && <ProjectOverviewTab project={currentProject} onChange={handleProjectUpdate} />}
                {activeTab === 1 && <ProjectGalleryTab project={currentProject} onChange={handleProjectUpdate} />}
                {activeTab === 2 && <ProjectAmenitiesTab project={currentProject} onChange={handleProjectUpdate} />}
                {activeTab === 3 && (
                  <ProjectReelsTab
                    project={currentProject}
                    reels={influencerVideos.filter((v) => v.projectSlug === currentProject.slug)}
                    onSaveReel={handleSaveInfluencerVideo}
                    onDeleteReel={handleDeleteInfluencerVideo}
                  />
                )}
                {activeTab === 4 && <ProjectSeoTab project={currentProject} onChange={handleProjectUpdate} />}
                {activeTab === 5 && <ProjectFaqTab project={currentProject} onChange={handleProjectUpdate} />}
              </Box>
            </Paper>
          )}
        </>
      )}

      {/* MAIN TAB 1: CUSTOMER TESTIMONIALS */}
      {mainTab === 1 && (
        <TestimonialsManagerTab
          testimonials={testimonials}
          onSave={handleSaveTestimonial}
          onDelete={handleDeleteTestimonial}
        />
      )}

      {/* MAIN TAB 2: INFLUENCER VIDEOS & REELS (GLOBAL MASTER) */}
      {mainTab === 2 && (
        <InfluencerVideosManagerTab
          videos={influencerVideos}
          projects={projects}
          onSave={handleSaveInfluencerVideo}
          onDelete={handleDeleteInfluencerVideo}
        />
      )}

      {/* MAIN TAB 3: PAST PROJECTS & HERITAGE */}
      {mainTab === 3 && (
        <PastProjectsManagerTab
          pastProjects={pastProjects}
          onSave={handleSavePastProject}
          onDelete={handleDeletePastProject}
        />
      )}

      {/* MAIN TAB 4: SITE SETTINGS & LIVE STATS */}
      {mainTab === 4 && siteSettings && (
        <SiteSettingsManagerTab
          settings={siteSettings}
          projects={projects}
          pastProjects={pastProjects}
          onSave={handleSaveSiteSettings}
          onDirty={setHasUnsavedChanges}
        />
      )}

      {/* New Project Modal */}
      <NewProjectModal
        open={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        onCreate={handleCreateNewProject}
        existingSlugs={projects.map((p) => p.slug)}
      />

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>CRM draft preview: {currentProject?.name}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>This preview uses the current editor fields. The public website may render them differently.</Alert>
          {currentProject && <>
            {currentProject.heroImage && <Box component="img" src={currentProject.heroImage} alt={currentProject.name} sx={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 2, mb: 2 }} />}
            <Typography variant="h4" sx={{ fontWeight: 800 }}>{currentProject.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary">{currentProject.tagline}</Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>{currentProject.location} · {currentProject.completionDate}</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>{currentProject.description}</Typography>
            <Typography variant="subtitle2" sx={{ mt: 3 }}>Search title</Typography>
            <Typography variant="body2">{currentProject.seo?.metaTitle || 'Missing'}</Typography>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>Search description</Typography>
            <Typography variant="body2">{currentProject.seo?.metaDescription || 'Missing'}</Typography>
          </>}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreviewOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity || 'success'}
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
